import React, { useEffect, useMemo, useCallback } from 'react'
import { AmenitiesHeader } from '../../../Amenities/AmenitiesHeader'
import { AmenitiesList } from '../../../Amenities/AmenitiesList'
import { AmenitiesDataSources } from '../../../Amenities/AmenitiesDataSources'
import {
  AmenitiesContainer,
  AmenitiesContentContainer,
} from '../../../Amenities/Amenities.styles'
import { AmenitiesLoading } from '../../../Amenities/AmenitiesLoading'
import { AmenitiesError } from '../../../Amenities/AmenitiesError'
import { useAnalysisMode } from '../../contexts/AnalysisModeContext'
import { useAnalysisServices } from '../../contexts/AnalysisServicesContext'
import { useAnalysisMapSectionData } from '../../contexts/MapSectionDataContext'

/**
 * Shared AmenitiesTab used by both main and public apps.
 *
 * - Main app: gets data from getSectionData('amenities'),
 *   supports POI click-to-fly
 * - Public app: gets data from entity.amenities,
 *   read-only display
 */
export const AmenitiesTab: React.FC = () => {
  const { entity, readOnly } = useAnalysisMode()
  const { getSectionData } = useAnalysisServices()
  const {
    setActiveSection,
    updateSectionData,
    clearSectionData,
    setSelectedItemId,
    setFlyToCoordinates,
  } = useAnalysisMapSectionData()

  // Get amenities data from context or entity
  const data = useMemo(() => {
    const contextData = getSectionData('amenities')
    if (contextData) return contextData
    return entity?.amenities || null
  }, [getSectionData, entity])

  // Extract livability score from multiple possible locations
  const livabilityScore = useMemo(() => {
    return (
      entity?.livability_score ||
      entity?.livabilityScore ||
      entity?.scores?.livability_score ||
      data?.overall_score?.value ||
      null
    )
  }, [entity, data])

  // Extract all POIs from data (handles multiple formats)
  const allPOIs = useMemo(() => {
    if (!data) return []

    // Format 1: flat pois array (public land)
    if (data.pois) return data.pois

    // Format 2: flat poi array (public estimate)
    if (data.poi) return data.poi

    // Format 3: category_scores with items
    if (data.category_scores) {
      return data.category_scores.flatMap((cs: any) =>
        (cs.items || []).map((poi: any) => ({
          ...poi,
          category: cs.category,
        }))
      )
    }

    // Format 4: raw array
    if (Array.isArray(data)) return data

    return []
  }, [data])

  // Push amenities data to map section context
  useEffect(() => {
    setActiveSection('amenities')

    if (allPOIs.length > 0) {
      updateSectionData({ amenities: allPOIs })
    }

    return () => {
      clearSectionData()
    }
  }, [allPOIs])

  // Handle POI click to fly to location on map
  const handlePOIClick = useCallback(
    (poi: any) => {
      if (poi?.coordinates) {
        setSelectedItemId(poi.id)
        const lon = poi.coordinates.longitude || poi.coordinates.lon
        const lat = poi.coordinates.latitude || poi.coordinates.lat
        if (lon && lat) {
          setFlyToCoordinates([lon, lat])
        }
      }
    },
    [setSelectedItemId, setFlyToCoordinates]
  )

  // Main app: show loading/error states
  if (!readOnly) {
    const isLoading = !data && !entity?.amenities
    if (isLoading && getSectionData('amenitiesLoading')) {
      return <AmenitiesLoading />
    }
  }

  if (!data) {
    if (!readOnly) {
      return (
        <AmenitiesError
          error="No amenities data available"
          onRetry={() => {}}
        />
      )
    }
    return null
  }

  return (
    <AmenitiesContainer>
      <AmenitiesHeader data={data} livabilityScore={livabilityScore} />
      <AmenitiesContentContainer>
        <AmenitiesList
          data={data}
          onPOIClick={readOnly ? undefined : handlePOIClick}
        />
        <AmenitiesDataSources
          sources={['OpenStreetMap', 'Overture Maps', 'ATLAS (öV)']}
        />
      </AmenitiesContentContainer>
    </AmenitiesContainer>
  )
}
